import { Typography } from '@euler/app/components/typography';
import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import {
  ApdmFunnelStep,
  apdmPercent,
} from '@euler/functions/apdm/campaignFunnel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { memo, useEffect, useRef, useState } from 'react';
import {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Pressable,
  ScrollView,
  View,
} from 'react-native';
import Svg, { Polygon } from 'react-native-svg';

/** How each step's share is said: of the first step, or of the one before. */
export type ApdmFunnelBasis = 'first' | 'previous';

function stepColor(step: ApdmFunnelStep): string {
  return step.outcome ? aiColors.green : aiColors.primary;
}

function share(steps: ApdmFunnelStep[], index: number): number {
  const first = steps[0]?.count ?? 0;
  return first ? Math.min(1, steps[index].count / first) : 0;
}

/** The share line of a step, e.g. `20.1%` or `回复的 40%`. */
function shareText(
  steps: ApdmFunnelStep[],
  index: number,
  basis: ApdmFunnelBasis,
): string {
  if (basis === 'first') return apdmPercent(steps[index].count, steps[0].count);
  if (index === 0) return '';
  if (index === 1) return apdmPercent(steps[1].count, steps[0].count);
  return `${steps[index - 1].label}的 ${apdmPercent(
    steps[index].count,
    steps[index - 1].count,
    0,
  )}`;
}

const kTrapezoidRow = 36;
// A step too small to see keeps this much width, so its number still fits.
const kTrapezoidFloor = 0.22;

// A step never draws wider than the one above it, even when it counts more
// (a lead can be credited before its reply is), so the funnel only narrows.
function trapezoidWidths(steps: ApdmFunnelStep[]): number[] {
  const widths: number[] = [];
  steps.forEach((_, index) => {
    const own = Math.max(kTrapezoidFloor, Math.sqrt(share(steps, index)));
    widths.push(index ? Math.min(widths[index - 1], own) : own);
  });
  return widths;
}

/** Steps as a funnel: each band narrows to the next one's width. */
const TrapezoidFunnel = memo(
  ({ steps, width }: { steps: ApdmFunnelStep[]; width: number }) => {
    const { rpx } = useResponsiveContext();
    const labelWidth = rpx(64);
    const shareWidth = rpx(52);
    const gap = rpx(8);
    const band = Math.max(0, width - labelWidth - shareWidth - gap * 2);
    const widths = trapezoidWidths(steps);
    return (
      <View style={{ gap: rpx(6) }}>
        {steps.map((step, index) => {
          const top = widths[index] * band;
          const bottom =
            index + 1 < steps.length ? widths[index + 1] * band : top * 0.9;
          const inset = (top - bottom) / 2;
          return (
            <View
              key={step.label}
              style={{ flexDirection: 'row', alignItems: 'center', gap }}
            >
              <Typography.Text
                size={rpx(12)}
                color={aiColors.mutedForeground}
                style={{ width: labelWidth }}
              >
                {step.label}
              </Typography.Text>
              <View
                style={{
                  width: band,
                  height: kTrapezoidRow,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Svg
                  width={top}
                  height={kTrapezoidRow}
                  style={{ position: 'absolute' }}
                >
                  <Polygon
                    points={`0,0 ${top},0 ${
                      top - inset
                    },${kTrapezoidRow} ${inset},${kTrapezoidRow}`}
                    fill={stepColor(step)}
                  />
                </Svg>
                <Typography.Text bold size={rpx(12)} color="#fff">
                  {step.count.toLocaleString('zh-CN')}
                </Typography.Text>
              </View>
              <Typography.Text
                bold
                size={rpx(12)}
                color={aiColors.foreground}
                style={{ width: shareWidth, textAlign: 'right' }}
              >
                {shareText(steps, index, 'first')}
              </Typography.Text>
            </View>
          );
        })}
      </View>
    );
  },
);

/** Steps as bars, each as long as its share of the first step. */
export const ApdmFunnelBars = memo(
  ({
    steps,
    basis = 'first',
  }: {
    steps: ApdmFunnelStep[];
    basis?: ApdmFunnelBasis;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View style={{ gap: rpx(12) }}>
        {steps.map((step, index) => {
          const note = shareText(steps, index, basis);
          return (
            <View key={step.label}>
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Typography.Text size={rpx(12)} color={aiColors.foreground}>
                  {step.label}
                </Typography.Text>
                <Typography.Text
                  bold
                  size={rpx(12)}
                  color={aiColors.foreground}
                >
                  {step.count.toLocaleString('zh-CN')}
                  {note ? (
                    <Typography.Text
                      size={rpx(12)}
                      color={aiColors.mutedForeground}
                    >
                      {basis === 'first' ? '  ' : ' · '}
                      {note}
                    </Typography.Text>
                  ) : null}
                </Typography.Text>
              </View>
              <View
                style={{
                  marginTop: rpx(4),
                  height: rpx(8),
                  borderRadius: rpx(4),
                  backgroundColor: aiColors.muted,
                  overflow: 'hidden',
                }}
              >
                <View
                  style={{
                    width: `${share(steps, index) * 100}%`,
                    minWidth: step.count ? rpx(4) : 0,
                    height: '100%',
                    borderRadius: rpx(4),
                    backgroundColor: stepColor(step),
                  }}
                />
              </View>
            </View>
          );
        })}
      </View>
    );
  },
);

/**
 * The funnel as a chart and as bars, swiped between (or picked by the dots,
 * since a mouse cannot swipe); the chart shows first.
 */
export const ApdmFunnelChart = memo(
  ({ steps }: { steps: ApdmFunnelStep[] }) => {
    const { rpx } = useResponsiveContext();
    const [width, setWidth] = useState(0);
    const [page, setPage] = useState(0);
    const scroller = useRef<ScrollView>(null);
    const settle = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => () => clearTimeout(settle.current ?? undefined), []);
    // The pages sit apart, so mid-swipe they never run into each other; a
    // page and the gap after it are one snap.
    const gap = rpx(32);
    const stride = width + gap;
    const onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (!width) return;
      const nearest = Math.round(event.nativeEvent.contentOffset.x / stride);
      setPage(nearest);
      // A trackpad or mouse wheel on the web stops wherever it stops; once
      // it rests, snap to a page.
      clearTimeout(settle.current ?? undefined);
      settle.current = setTimeout(
        () => scroller.current?.scrollTo({ x: nearest * stride }),
        150,
      );
    };
    return (
      <View
        onLayout={(event: LayoutChangeEvent) =>
          setWidth(event.nativeEvent.layout.width)
        }
      >
        {width ? (
          <ScrollView
            ref={scroller}
            horizontal
            snapToInterval={stride}
            snapToAlignment="start"
            decelerationRate="fast"
            disableIntervalMomentum
            contentContainerStyle={{ gap }}
            showsHorizontalScrollIndicator={false}
            onScroll={onScroll}
            scrollEventThrottle={32}
          >
            <View style={{ width }}>
              <TrapezoidFunnel steps={steps} width={width} />
            </View>
            <View style={{ width, justifyContent: 'center' }}>
              <ApdmFunnelBars steps={steps} />
            </View>
          </ScrollView>
        ) : null}
        <View
          accessibilityLabel={page === 0 ? '漏斗图' : '条形图'}
          style={{
            flexDirection: 'row',
            justifyContent: 'center',
            gap: rpx(6),
            marginTop: rpx(12),
          }}
        >
          {[0, 1].map(index => (
            <Pressable
              key={index}
              accessibilityRole="button"
              accessibilityLabel={index === 0 ? '看漏斗图' : '看条形图'}
              hitSlop={8}
              onPress={() => {
                setPage(index);
                scroller.current?.scrollTo({ x: index * stride });
              }}
              style={{
                width: index === page ? rpx(16) : rpx(6),
                height: rpx(6),
                borderRadius: rpx(3),
                backgroundColor:
                  index === page
                    ? aiColors.primary
                    : tint(aiColors.mutedForeground, 0.3),
              }}
            />
          ))}
        </View>
      </View>
    );
  },
);
